import { useEffect, useMemo, useState } from 'react';
import { api, type CutappPriceMapping, type CutappRounding } from '../../services/api';
import { cutappPriceSyncBreakdown } from '../shared/CutappProductSync';

type Marketplace = 'shopee' | 'mercadolivre';

const MARKETPLACES: { id: Marketplace; label: string; hint: string }[] = [
  { id: 'shopee', label: 'Shopee', hint: 'O Minimo e o preco que entra na oferta relampago.' },
  { id: 'mercadolivre', label: 'Mercado Livre', hint: 'O Minimo e o piso: campanha abaixo dele e recusada (below_min_price).' },
];

const MIN_FLASH_DISCOUNT_PCT = 10;

function defaultMapping(marketplace: Marketplace): CutappPriceMapping {
  return {
    marketplace,
    basePriceFrom: 'sugerido',
    campaignPriceFrom: 'valor',
    flashDiscountPct: 10,
    flashDiscountMode: 'manual',
    rounding: { mode: 'none', step: 1 },
    enabled: false,
  };
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const normalized = typeof value === 'string'
    ? value.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.')
    : value;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function formatCurrency(value?: number | null) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function applyRounding(value: number | null, rounding?: CutappRounding): number | null {
  if (value === null || !Number.isFinite(value)) return value;
  const mode = rounding?.mode || 'none';
  const step = Number(rounding?.step) > 0 ? Number(rounding?.step) : 1;
  if (mode === 'floor') return Math.floor(value / step) * step;
  if (mode === 'floor90' || mode === 'floor99') {
    const cents = mode === 'floor90' ? 0.9 : 0.99;
    let candidate = Math.floor(value) + cents;
    if (candidate > value) candidate -= 1;
    return Math.max(0, Number(candidate.toFixed(2)));
  }
  return value;
}

export function SellerPriceSourcePanel() {
  const [mappings, setMappings] = useState<CutappPriceMapping[]>(() => MARKETPLACES.map((m) => defaultMapping(m.id)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.getCutappPriceMappings();
        const list: any[] = Array.isArray(res) ? res : res?.mappings || res?.data || [];
        setMappings(MARKETPLACES.map((m) => {
          const found = list.find((item: any) => item?.marketplace === m.id);
          return found ? { ...defaultMapping(m.id), ...found, rounding: { ...defaultMapping(m.id).rounding, ...(found.rounding || {}) } } : defaultMapping(m.id);
        }));
      } catch (err: any) {
        setError(err?.message || 'Nao foi possivel carregar o mapeamento de precos do Cut App.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const patchMapping = (marketplace: Marketplace, patch: Partial<CutappPriceMapping>) => {
    setDirty(true);
    setNotice(null);
    setMappings((current) => current.map((item) => item.marketplace === marketplace ? { ...item, ...patch } : item));
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    for (const mapping of mappings) {
      if (!mapping.enabled) continue;
      // Margem minima do Cut App: o Minimo vem pronto por produto, o % nao e usado.
      if (mapping.flashDiscountMode === 'cutapp_minimo') continue;
      const label = MARKETPLACES.find((m) => m.id === mapping.marketplace)?.label || mapping.marketplace;
      const pct = toNumber(mapping.flashDiscountPct);
      if (pct === null) {
        errors.push(`${label}: informe o % de desconto para a campanha relampago. Sem ele o Minimo fica vazio e bloqueia qualquer campanha (missing_min_price).`);
      } else if (pct < MIN_FLASH_DISCOUNT_PCT) {
        errors.push(`${label}: o desconto minimo e ${MIN_FLASH_DISCOUNT_PCT}%. Valor informado: ${pct}%.`);
      } else if (pct >= 100) {
        errors.push(`${label}: o desconto precisa ser menor que 100%.`);
      }
    }
    return errors;
  }, [mappings]);

  const save = async () => {
    if (validationErrors.length > 0) { setError(validationErrors[0]); return; }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload = mappings.map((mapping) => ({
        ...mapping,
        flashDiscountPct: toNumber(mapping.flashDiscountPct),
        rounding: { mode: mapping.rounding?.mode || 'none', step: Number(mapping.rounding?.step) > 0 ? Number(mapping.rounding?.step) : 1 },
      }));
      await api.saveCutappPriceMappings(payload as CutappPriceMapping[]);
      setDirty(false);
      // "Aplicar" de verdade: reprocessa a tabela de precos com o mapeamento novo.
      // Sem nenhum marketplace ativo nao ha o que aplicar (o sync responde no_mapping).
      if (!mappings.some((mapping) => mapping.enabled)) {
        setNotice('Mapeamento salvo. Nenhum marketplace ativo: os precos do Seller nao foram alterados.');
        return;
      }
      try {
        const res: any = await api.runCutappSyncPrices();
        const report = res?.report ?? res?.result ?? res;
        const breakdown = cutappPriceSyncBreakdown(report);
        setNotice(`Mapeamento salvo e aplicado: ${report?.created || 0} criado(s), ${report?.updated || 0} atualizado(s), ${report?.unchanged || 0} sem mudanca. ${breakdown}`.trim());
      } catch (syncErr: any) {
        setNotice('Mapeamento salvo.');
        setError(`Nao foi possivel aplicar agora: ${syncErr?.message || 'erro no sync de precos'}. A proxima sincronizacao aplica o mapeamento novo.`);
      }
    } catch (err: any) {
      setError(err?.message || 'Erro ao salvar o mapeamento de precos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Mapeamento de precos Cut App</h2>
        <p className="mt-1 text-xs leading-5 text-gray-500">
          Define qual preco do Cut App alimenta cada papel da tabela de precos. A Integraia nao recalcula nada: taxa,
          markup e gross-up ficam no Cut App. Campos travados com o cadeado na grade nunca sao sobrescritos pelo sync.
        </p>

        <div className="mt-4 rounded-md border-2 border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-bold text-blue-900">
            O desconto incide sobre o PRECO DE CAMPANHA, nao sobre o preco base
          </p>
          <p className="mt-2 text-sm leading-6 text-blue-900">
            O preco de campanha e o <strong>preco normal de marketplace do Cut App</strong> (o campo <code>valor</code>,
            que ja embute a taxa da plataforma e a margem da lane). No modo <strong>Percentual manual</strong> o Minimo e
            {' '}<strong>Campanha &times; (1 &minus; X%)</strong> &mdash; nunca a partir do preco base. No modo
            {' '}<strong>Margem minima do Cut App</strong> o Minimo e o preco minimo que o Cut App calcula para cada produto.
          </p>
          <div className="mt-3 grid gap-2 text-xs md:grid-cols-4">
            <div className="rounded-md bg-white px-3 py-2">
              <div className="font-semibold text-gray-500">valor (Cut App)</div>
              <div className="text-base font-bold text-gray-900">R$ 100,00</div>
            </div>
            <div className="rounded-md bg-white px-3 py-2">
              <div className="font-semibold text-gray-500">Base &larr; sugerido</div>
              <div className="text-base font-bold text-gray-900">R$ 130,00</div>
            </div>
            <div className="rounded-md bg-white px-3 py-2">
              <div className="font-semibold text-gray-500">Campanha &larr; valor</div>
              <div className="text-base font-bold text-gray-900">R$ 100,00</div>
            </div>
            <div className="rounded-md bg-white px-3 py-2">
              <div className="font-semibold text-gray-500">Minimo = Campanha &minus; 10%</div>
              <div className="text-base font-bold text-emerald-700">R$ 90,00</div>
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-blue-900">
            Com <strong>valor 100 &rarr; Base 130 &rarr; Minimo 90 com 10%</strong> a ordem
            {' '}<strong>Minimo &le; Campanha &le; Base</strong> fica garantida. O Minimo e o preco que vai para a
            {' '}<strong>oferta relampago da Shopee</strong> e serve de <strong>piso no Mercado Livre</strong> (campanha
            abaixo dele e recusada). Descontar sobre a base geraria um relampago muito mais barato do que o pretendido.
          </p>
        </div>

        {error && <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {notice && <div className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{notice}</div>}
        {validationErrors.length > 0 && (
          <ul className="mt-4 space-y-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {validationErrors.map((message) => <li key={message}>⚠️ {message}</li>)}
          </ul>
        )}

        {loading ? (
          <p className="mt-4 text-sm text-gray-500">Carregando mapeamento...</p>
        ) : (
          <div className="mt-4 space-y-3">
            {mappings.map((mapping) => {
              const meta = MARKETPLACES.find((m) => m.id === mapping.marketplace);
              const pct = toNumber(mapping.flashDiscountPct);
              const exampleMin = applyRounding(100 * (1 - (pct ?? 0) / 100), mapping.rounding);
              const usaMinimoCutapp = mapping.flashDiscountMode === 'cutapp_minimo';
              const pctInvalido = !usaMinimoCutapp && pct !== null && pct < MIN_FLASH_DISCOUNT_PCT;
              return (
                // Um card por marketplace. Antes era uma tabela com min-w-[900px], que
                // forcava barra de rolagem lateral dentro da pagina da conexao.
                <div key={mapping.marketplace} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{meta?.label}</div>
                      <div className="mt-0.5 text-xs text-gray-500">{meta?.hint}</div>
                    </div>
                    <label className="flex shrink-0 items-center gap-2 rounded-md bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!mapping.enabled}
                        onChange={(e) => patchMapping(mapping.marketplace, { enabled: e.target.checked })}
                        className="h-4 w-4"
                      />
                      Ativo
                    </label>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Preco base</label>
                      <select
                        value={mapping.basePriceFrom}
                        onChange={(e) => patchMapping(mapping.marketplace, { basePriceFrom: e.target.value as CutappPriceMapping['basePriceFrom'] })}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                      >
                        <option value="sugerido">sugerido (preco de tabela)</option>
                        <option value="valor">valor (preco normal)</option>
                        <option value="precoFinal">precoFinal (fallback)</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Preco de campanha</label>
                      <select
                        value={mapping.campaignPriceFrom}
                        onChange={(e) => patchMapping(mapping.marketplace, { campaignPriceFrom: e.target.value as CutappPriceMapping['campaignPriceFrom'] })}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                      >
                        <option value="valor">valor (preco normal de marketplace)</option>
                        <option value="sugerido">sugerido (preco de tabela)</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Desconto relampago</label>
                      <select
                        value={mapping.flashDiscountMode || 'manual'}
                        onChange={(e) => patchMapping(mapping.marketplace, { flashDiscountMode: e.target.value as CutappPriceMapping['flashDiscountMode'] })}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                      >
                        <option value="manual">Percentual manual</option>
                        <option value="cutapp_minimo">Margem minima do Cut App</option>
                      </select>
                      {usaMinimoCutapp ? (
                        <p className="mt-1 text-[11px] text-gray-500">
                          O Minimo e o <strong>preco minimo que o Cut App calcula</strong> com a margem minima que voce setou em cada produto
                          (ja com as taxas de {meta?.label}). Produto sem margem minima fica pendente.
                          {mapping.marketplace === 'shopee' && <> A Shopee exige pelo menos {MIN_FLASH_DISCOUNT_PCT}% de desconto: se a margem minima der menos, o produto fica pendente.</>}
                        </p>
                      ) : (
                        <>
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              value={mapping.flashDiscountPct ?? ''}
                              onChange={(e) => patchMapping(mapping.marketplace, { flashDiscountPct: e.target.value })}
                              inputMode="decimal"
                              className={`w-full rounded-md border px-2 py-1.5 text-xs ${pctInvalido ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                              placeholder="10"
                            />
                            <span className="text-xs text-gray-500">%</span>
                          </div>
                          <p className="mt-1 text-[11px] text-gray-500">
                            Sobre a <strong>Campanha</strong>: 100 &minus; {pct ?? 0}% = {formatCurrency(exampleMin)}
                          </p>
                          <p className="text-[11px] text-gray-400">Minimo permitido: {MIN_FLASH_DISCOUNT_PCT}%. Se furar a margem minima do Cut App, o produto fica pendente.</p>
                        </>
                      )}
                    </div>

                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Arredondamento</label>
                      <select
                        value={mapping.rounding?.mode || 'none'}
                        onChange={(e) => patchMapping(mapping.marketplace, { rounding: { ...(mapping.rounding || {}), mode: e.target.value as CutappRounding['mode'] } })}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                      >
                        <option value="none">Sem arredondamento</option>
                        <option value="floor">Para baixo no passo</option>
                        <option value="floor90">Para baixo terminando em ,90</option>
                        <option value="floor99">Para baixo terminando em ,99</option>
                      </select>
                      {mapping.rounding?.mode === 'floor' && (
                        <input
                          value={mapping.rounding?.step ?? 1}
                          onChange={(e) => patchMapping(mapping.marketplace, { rounding: { ...(mapping.rounding || { mode: 'floor' }), step: Number(e.target.value) || 1 } })}
                          inputMode="decimal"
                          className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                          placeholder="passo"
                        />
                      )}
                      <p className="mt-1 text-[11px] text-gray-400">Sempre para baixo, para nao subir o preco do relampago.</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || validationErrors.length > 0}
            className="rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar e aplicar mapeamento'}
          </button>
          {dirty && <span className="text-xs font-medium text-amber-700">Alteracoes nao salvas.</span>}
        </div>
      </section>

    </div>
  );
}
